import { useCallback, useMemo, useState } from "react";

export type QueueFile = {
  id: string;
  file: File;
  displayName: string;
  relativePath: string;
  status: "ready" | "too_large" | "duplicate";
};

function makeId() {
  return crypto.randomUUID();
}

function normalizePath(file: File): string {
  const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath;
  return rel || file.name;
}

export function useUploadQueue(maxFileSizeBytes: number) {
  const [items, setItems] = useState<QueueFile[]>([]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files);
      setItems((prev) => {
        const names = new Set(prev.map((p) => p.relativePath));
        const next = [...prev];
        for (const file of incoming) {
          const relativePath = normalizePath(file);
          const duplicate = names.has(relativePath);
          const tooLarge = file.size > maxFileSizeBytes;
          next.push({
            id: makeId(),
            file,
            displayName: relativePath.split("/").pop() ?? file.name,
            relativePath,
            status: duplicate ? "duplicate" : tooLarge ? "too_large" : "ready",
          });
          if (!duplicate) names.add(relativePath);
        }
        return next;
      });
    },
    [maxFileSizeBytes],
  );

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const rename = useCallback((id: string, displayName: string) => {
    setItems((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const trimmed = displayName.trim() || f.displayName;
        const slash = f.relativePath.lastIndexOf("/");
        const relativePath =
          slash >= 0 ? `${f.relativePath.slice(0, slash + 1)}${trimmed}` : trimmed;
        return { ...f, displayName: trimmed, relativePath };
      }),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const readyFiles = useMemo(
    () => items.filter((f) => f.status === "ready"),
    [items],
  );

  const stats = useMemo(() => {
    const totalBytes = readyFiles.reduce((n, f) => n + f.file.size, 0);
    const issues = items.filter((f) => f.status !== "ready").length;
    return { count: readyFiles.length, totalBytes, issues };
  }, [items, readyFiles]);

  return { items, readyFiles, stats, addFiles, remove, rename, clear };
}
